import { createTheme } from "@mui/material/styles";

// Business Flows design system on Material UI (MUI v5).
// Brand: navy ink from the product badge, yellow from the logo.
export const brand = {
  ink: "#1a1a2e",
  inkSoft: "#34344f",
  // One step deeper than ink: the navy a brand panel is painted in, so the
  // white card sitting on it still reads as the thing in front.
  inkDeep: "#14142a",
  signal: "#f0e631",
  signalDeep: "#8f8a1d",
  canvas: "#f6f7f9",
  line: "#e3e5ea",
  muted: "#5d6472",
  // Long-form reading: a shade darker than muted, so a paragraph of guidance
  // stays comfortable at 72 characters a line without shouting like a heading.
  prose: "#3f4557",
  // A surface one step quieter than white, for a row or a panel *inside* a
  // white card - where `canvas` would disappear against the page behind it.
  shell: "#fafbfc",
  // A hairline for a division inside a card, lighter than the card's own edge.
  hairline: "#eef0f4",
  // The brand yellow has one job: it marks. Never a fill behind a sentence -
  // a tint behind the thing being marked, so it points without shouting.
  // `mark` sits behind a chip or an icon; `markSoft` washes a whole row or
  // cell, where the stronger tint would read as a highlighter pen.
  mark: "rgba(240, 230, 49, 0.35)",
  markSoft: "rgba(240, 230, 49, 0.16)",
};

// One rounding scale, so a panel on one screen matches a panel on the next.
// The numbers are MUI `sx` multipliers of `shape.borderRadius` (10px) - write
// `borderRadius: radius.surface`, never a bare number, and the explorer, search
// and home read as one product rather than three.
export const radius = {
  // A button-sized well: a toolbar group, a pill, a small tile.
  control: 2,
  // One object: a search result, a node's card, a row that can be picked.
  card: 3,
  // A page-level surface: a panel, a canvas, a dialog, a launch tile.
  surface: 4,
};

// Lift, in three steps, so "this is picked", "the pointer is over this" and
// "this floats above the page" look the same on every screen. Always the
// brand's ink, never a grey or a black.
export const shadow = {
  picked: "0 12px 28px -18px rgba(26, 26, 46, 0.5)",
  raised: "0 18px 36px -20px rgba(26, 26, 46, 0.45)",
  floating: "0 24px 48px -20px rgba(26, 26, 46, 0.45)",
};

// How a screen says "this went well", "change this", "wait a moment" or
// "nothing to do here". Tinted paper and a hairline rather than a filled bar:
// a notice a person reads, not an alarm. `fg` carries the icon and the
// heading and passes AA on its own `bg`.
export const notice = {
  good: { fg: "#14663f", bg: "#eef6f1", line: "#cbe3d6" },
  // Clay rather than fire-engine red: most "errors" here are a mistyped key,
  // not damage, and the page should not shout at someone who pasted badly.
  fix: { fg: "#8c3a2b", bg: "#fbf1ee", line: "#eed8d1" },
  wait: { fg: "#7d541a", bg: "#fbf4e8", line: "#efe0c6" },
  quiet: { fg: brand.inkSoft, bg: "#f2f3f7", line: brand.line },
};

const fontFamily = '"Geist", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

const businessFlowsTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: brand.ink, light: brand.inkSoft, dark: "#0f0f1d", contrastText: "#ffffff" },
    secondary: { main: brand.signal, dark: brand.signalDeep, contrastText: brand.ink },
    background: { default: brand.canvas, paper: "#ffffff" },
    text: { primary: brand.ink, secondary: brand.muted },
    divider: brand.line,
    error: { main: "#b42318" },
    success: { main: "#0f7b43" },
    info: { main: "#2f5bd3" },
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily,
    h1: { fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.1 },
    h2: { fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.12 },
    h3: { fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.15 },
    h4: { fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.2 },
    h5: { fontWeight: 650, letterSpacing: "-0.01em" },
    subtitle1: { color: brand.muted, lineHeight: 1.55 },
    body2: { lineHeight: 1.55 },
    button: { textTransform: "none", fontWeight: 600, letterSpacing: 0 },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 10 },
        sizeLarge: { minHeight: 48, fontSize: "1rem", paddingInline: 20 },
        outlined: { borderColor: brand.line },
      },
    },
    MuiLoadingButton: {
      defaultProps: { disableElevation: true },
    },
    MuiTextField: {
      defaultProps: { variant: "outlined", fullWidth: true },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: "#fff",
          transition: "box-shadow 150ms",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cfd3da" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#9ea4ae" },
          "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(240, 230, 49, 0.55)" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: brand.ink, borderWidth: 1.5 },
        },
        input: { paddingTop: 14, paddingBottom: 14 },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { "&.Mui-focused": { color: brand.ink } },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10, alignItems: "flex-start" },
        message: { lineHeight: 1.5 },
      },
    },
    MuiLink: {
      defaultProps: { underline: "hover" },
      styleOverrides: {
        root: { fontWeight: 600, color: brand.ink },
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: "#9ea4ae", "&.Mui-checked": { color: brand.ink } } },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: { "&.Mui-active": { color: brand.ink }, "&.Mui-completed": { color: brand.signalDeep } },
      },
    },
  },
});

export default businessFlowsTheme;
